/**
 * RecensioneForm Component - Hedita Design System
 * Feature: Recensioni - Form creazione/modifica
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, ArrowLeft, CheckCircle, AlertCircle, Save, MessageSquare } from "lucide-react";
import { useRecensione, useCreateRecensione, useUpdateRecensione } from "../hooks";
import { useStrutture } from "@/features/strutture";
import { usePrenotazioni } from "@/features/prenotazioni";
import { useQueryClient } from "@tanstack/react-query";

const empty = { 
  ospite_nome: "", 
  voto: 5, 
  testo: "", 
  piattaforma: "Diretto", 
  data: new Date().toISOString().split('T')[0],
  struttura_id: "",
  prenotazione_id: "",
  risposta: ""
};

const PIATTAFORME = ["Diretto", "Airbnb", "Booking.com", "Vrbo", "Expedia", "TripAdvisor", "Altro"];

export function RecensioneForm({ id, onSaved }: { id?: string; onSaved?: () => void }) {
  const isNew = !id || id === "nuovo";
  const navigate = useNavigate();
  
  const { data: recensione, isLoading } = useRecensione(isNew ? undefined : id);
  const { data: strutture = [] } = useStrutture();
  const { data: prenotazioni = [] } = usePrenotazioni();
  const createMut = useCreateRecensione();
  const updateMut = useUpdateRecensione();

  const [form, setForm] = useState(empty);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (recensione) {
      setForm({
        ospite_nome: recensione.ospite_nome || "",
        voto: recensione.voto || 5,
        testo: recensione.testo || "",
        piattaforma: recensione.piattaforma || "Diretto",
        data: recensione.data || new Date().toISOString().split('T')[0],
        struttura_id: recensione.struttura_id || "",
        prenotazione_id: recensione.prenotazione_id || "",
        risposta: recensione.risposta || "",
      });
    }
  }, [recensione]);

  const set = (k: keyof typeof form, v: string | number) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.ospite_nome.trim()) return;
    
    setSaveStatus("saving");
    setSaveError(null);

    try {
      if (isNew) {
        await createMut.mutateAsync(form);
      } else {
        await updateMut.mutateAsync({ id: id!, data: form });
      }
      
      setSaveStatus("saved");
      setTimeout(() => {
        setSaveStatus("idle");
        onSaved?.();
        navigate("/recensioni");
      }, 1000);
    } catch (error) {
      setSaveStatus("error");
      setSaveError(error instanceof Error ? error.message : "Errore durante il salvataggio");
    }
  };

  const renderStars = (rating: number, onChange?: (rating: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-6 w-6 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} ${onChange ? 'cursor-pointer hover:text-yellow-400' : ''}`}
            onClick={() => onChange?.(star)}
          />
        ))}
      </div>
    );
  };

  const isLoadingData = isLoading && !isNew;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/recensioni")}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-400" />
                  {isNew ? "Nuova Recensione" : "Modifica Recensione"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isNew ? "Aggiungi una nuova recensione" : "Modifica la recensione esistente"}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/recensioni")}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={!form.ospite_nome.trim() || saveStatus === "saving"}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveStatus === "saving" ? (
                  <>
                    <div className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    Salvataggio...
                  </>
                ) : saveStatus === "saved" ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Salvato
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Salva
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {isLoadingData ? (
          <div className="space-y-6">
            <div className="animate-pulse">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-10 bg-muted rounded-xl" />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Nome Ospite */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Nome Ospite *
              </label>
              <input
                type="text"
                value={form.ospite_nome}
                onChange={(e) => set("ospite_nome", e.target.value)}
                placeholder="Nome dell'ospite"
                className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
            </div>

            {/* Voto */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Voto *
              </label>
              {renderStars(form.voto, (rating) => set("voto", rating))}
            </div>

            {/* Testo Recensione */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Testo Recensione
              </label>
              <textarea
                value={form.testo}
                onChange={(e) => set("testo", e.target.value)}
                placeholder="Testo della recensione"
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
              />
            </div>

            {/* Piattaforma e Data */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Piattaforma */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Piattaforma
                </label>
                <select
                  value={form.piattaforma}
                  onChange={(e) => set("piattaforma", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                >
                  {PIATTAFORME.map(piattaforma => (
                    <option key={piattaforma} value={piattaforma}>{piattaforma}</option>
                  ))}
                </select>
              </div>

              {/* Data */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Data
                </label>
                <input
                  type="date"
                  value={form.data}
                  onChange={(e) => set("data", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                />
              </div>
            </div>

            {/* Riferimenti */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Struttura */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Struttura
                </label>
                <select
                  value={form.struttura_id}
                  onChange={(e) => set("struttura_id", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                >
                  <option value="">Nessuna</option>
                  {strutture.map(struttura => (
                    <option key={struttura.id} value={struttura.id}>
                      {struttura.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Prenotazione */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Prenotazione
                </label>
                <select
                  value={form.prenotazione_id}
                  onChange={(e) => set("prenotazione_id", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                >
                  <option value="">Nessuna</option>
                  {prenotazioni.map(prenotazione => (
                    <option key={prenotazione.id} value={prenotazione.id}>
                      {prenotazione.ospite_nome} - {prenotazione.struttura_nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Risposta */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Risposta
              </label>
              <textarea
                value={form.risposta}
                onChange={(e) => set("risposta", e.target.value)}
                placeholder="Risposta alla recensione"
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
              />
            </div>

            {/* Error */}
            {saveError && (
              <div className="flex items-center gap-2 p-4 rounded-xl bg-destructive/10 border border-destructive/20">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">{saveError}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
